import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { profileData } from '../mock';

const stats = [
  { num: '15', plus: true, label: 'Years\nExperience' },
  { num: '21', plus: true, label: 'Film & TV Projects\nDeveloped' },
  { num: '72', plus: true, label: 'Events & Programs\nDelivered' },
  { num: '5', plus: true, label: 'Film Festivals &\nIndustry Programs' },
  { num: '5', plus: false, label: 'Languages' },
];

// --- Diagonal geometry ---
// Hero is exactly 100vh, About is exactly 60vh. Combined wrapper = 160vh.
// One continuous straight line: 70% at the very top of Hero -> 50% at the very bottom of About.
// Slope = (70 - 50) / 160vh = 0.125% per vh (a true straight line).
// Hero spans 100vh of that line: 70% (top) -> 57.5% (bottom) — drop of 12.5% over 100vh.
// About spans 60vh : 57.5% (top) -> 50% (bottom) — drop of 7.5% over 60vh.
// Both rates equal 0.125%/vh — guaranteed single straight line.

const Hero = () => {
  const navigate = useNavigate();
  const [currentRole, setCurrentRole] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % profileData.roles.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen lg:h-screen overflow-hidden bg-midnight-blue"
      data-testid="hero-section"
    >
      {/* === LEFT panel : dark midnight-blue with patterned backdrop (vertical edge) === */}
      <div
        className="hero-left-clip absolute left-0 top-[72px] bottom-0 right-0 bg-midnight-blue"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.025] md:opacity-[0.06]"
          style={{
            backgroundImage: 'url(/hero-pattern.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '760px auto',
            backgroundPosition: 'top left',
          }}
          data-testid="hero-pattern-backdrop"
        />
      </div>

      {/* === RIGHT panel : lighter steel-blue === */}
      <div
        className="absolute left-0 top-[72px] bottom-0 right-0 hidden lg:block bg-terracotta"
        style={{
          clipPath: 'polygon(68% 0, 100% 0, 100% 100%, 68% 100%)',
          WebkitClipPath: 'polygon(68% 0, 100% 0, 100% 100%, 68% 100%)',
        }}
        data-testid="hero-right-panel"
      />

      {/* === Oblique divider line (continues into About at exactly the same slope) === */}
      <svg
        aria-hidden="true"
        className="hidden lg:block absolute top-[72px] left-0 right-0 bottom-0 w-full h-[calc(100%-72px)] z-20 pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        data-testid="hero-divider"
      >
        <line x1="68" y1="0" x2="68" y2="100" stroke="rgba(255,255,255,0.3)" strokeWidth="0.15" vectorEffect="non-scaling-stroke" />
      </svg>

      {/* === CONTENT GRID === */}
      <div className="relative z-10 h-full">
        {/* On mobile we leave extra room below the cream header so the
            "Carol Mezher" H1 has breathing room instead of sitting flush
            against the menu. Desktop stays at pt-[72px] to keep the
            content aligned with the hero pattern start. */}
        <div className="container mx-auto px-8 md:px-16 h-full pt-[120px] md:pt-[72px]">
          <div className="grid grid-cols-1 lg:grid-cols-[70fr_30fr] gap-0 h-full items-center">
            {/* LEFT: name, role, tagline, CTAs */}
            <div className="text-left lg:pl-[1cm] lg:pr-12" data-testid="hero-left-content">
              {/* Desktop-only: pull the H1 (and the entire left text
                  column with it) up by 1 cm so the top of "Carol Mezher"
                  lines up with the top of "15+" on the right stats list.
                  Mobile is untouched — the -mt only applies at lg+. */}
              <h1 className="font-playfair font-normal text-left text-[clamp(3.5rem,8vw,7.5rem)] leading-[1] mb-7 text-white tracking-tight -ml-1 lg:-mt-[2.5cm]">
                Carol Mezher
              </h1>

              <div className="relative mb-8">
                <div className="absolute -left-5 top-0 bottom-0 w-[3px] bg-gradient-to-b from-terracotta to-whale-blue"></div>
                <div className="h-12 sm:h-14 lg:h-16 flex items-center">
                  <p
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-montserrat font-medium text-terracotta transition-opacity duration-300"
                    data-testid="hero-rotating-role"
                  >
                    {profileData.roles[currentRole]}
                  </p>
                </div>
              </div>

              <p className="text-lg md:text-xl lg:text-[1.55rem] text-white/95 text-justify mb-4 md:mb-10 !leading-relaxed max-w-[720px]">
                Multidisciplinary Content Director &amp; Creative Producer shaping projects from concept to audience across film, media, and live experiences.
              </p>

              {/* MOBILE — festival-program ghost pair.
                  Replaces the two equal-weight solid burgundy buttons that
                  read as a SaaS pricing block. Transparent + thin
                  terracotta border + leading "Work" / "Talk" tag + italic
                  serif label reframes the actions as program tabs. */}
              <div className="md:hidden space-y-3">
                <button
                  onClick={() => navigate('/projects')}
                  className="w-full flex items-baseline justify-between px-6 py-5 border border-terracotta/72 hover:bg-whale-blue/27 transition-colors group rounded-sm"
                  data-testid="hero-explore-work-btn"
                >
                  <span className="font-playfair italic text-xl text-white">Explore My Work</span>
                  <span className="text-terracotta transition-transform group-hover:translate-x-1">→</span>
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full flex items-baseline justify-between px-6 py-5 border border-terracotta/72 hover:bg-whale-blue/27 transition-colors group rounded-sm"
                  data-testid="hero-get-in-touch-btn"
                >
                  <span className="font-playfair italic text-xl text-white">Get in Touch</span>
                  <span className="text-terracotta transition-transform group-hover:translate-x-1">→</span>
                </button>
              </div>

              {/* DESKTOP — unchanged solid burgundy buttons. */}
              <div className="hidden md:flex flex-col sm:flex-row gap-4 justify-start items-stretch sm:items-center">
                <Button
                  onClick={() => navigate('/projects')}
                  className="bg-whale-blue hover:bg-whale-blue/87 text-white px-10 py-7 text-base md:text-lg font-montserrat font-bold transition-smooth shadow-lg hover:shadow-xl rounded-md w-full sm:w-auto"
                >
                  Explore My Work
                </Button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  className="bg-whale-blue hover:bg-whale-blue/87 text-white px-10 py-7 text-base md:text-lg font-montserrat font-bold transition-smooth shadow-lg hover:shadow-xl rounded-md w-full sm:w-auto"
                >
                  Get in Touch
                </Button>
              </div>

              <div className="mt-4 md:mt-10">
                <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-white/70 font-montserrat font-bold text-center md:text-left">
                  Film • Media • Entertainment • Events • Culture
                </p>
              </div>
            </div>

            {/* RIGHT: stats list */}
            <div
              className="hidden lg:flex flex-col items-start gap-6 text-left pl-16 pr-8 -mt-[70px]"
              data-testid="hero-right-stats"
            >
              {stats.map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-0"
                  data-testid={`hero-stat-${idx}`}
                >
                  <div className="font-playfair font-bold text-whale-blue leading-none flex items-start justify-end min-w-[120px]">
                    <span className="text-5xl md:text-6xl">{s.num}</span>
                    <span
                      className="text-2xl md:text-3xl mt-1.5 ml-1 leading-none"
                      style={{ visibility: s.plus ? 'visible' : 'hidden' }}
                      aria-hidden={!s.plus}
                    >
                      +
                    </span>
                  </div>
                  <div
                    className="text-base md:text-lg text-white font-montserrat whitespace-nowrap"
                    style={{
                      marginTop: '34px',
                      marginLeft: '-8px',
                      lineHeight: '1.05',
                    }}
                  >
                    {s.label.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile-only stats */}
            <div
              className="lg:hidden grid grid-cols-2 gap-x-4 gap-y-5 pb-12 mt-8"
              data-testid="hero-stats-mobile"
            >
              {stats.map((s, idx) => (
                <div
                  key={idx}
                  className={`flex items-baseline gap-3 ${idx === stats.length - 1 && stats.length % 2 === 1 ? 'col-span-2 justify-center' : ''}`}
                >
                  {/* text-shadow lifts the burgundy digits off the dark
                      midnight-blue + diamond pattern background so they
                      read crisply without changing color or weight. */}
                  <div
                    className="text-3xl font-playfair font-bold text-whale-blue leading-none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                  >
                    {s.num}{s.plus ? '+' : ''}
                  </div>
                  <div className="text-xs text-white/80 font-montserrat leading-tight whitespace-pre-line">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
